import React, { useState, useRef, useCallback } from 'react';
import { Phone, Delete } from 'lucide-react';
import { colors, radii, FONT } from '../../theme';

const KEYS = [
  [{ digit: '1', sub: '' }, { digit: '2', sub: 'ABC' }, { digit: '3', sub: 'DEF' }],
  [{ digit: '4', sub: 'GHI' }, { digit: '5', sub: 'JKL' }, { digit: '6', sub: 'MNO' }],
  [{ digit: '7', sub: 'PQRS' }, { digit: '8', sub: 'TUV' }, { digit: '9', sub: 'WXYZ' }],
  [{ digit: '*', sub: '' }, { digit: '0', sub: '+' }, { digit: '#', sub: '' }],
];

const LONG_PRESS_MS = 500;

// Strip everything except digits, +, *, #
const cleanPhone = (raw) => raw.replace(/[^\d+*#]/g, '');

export default function Dialpad({ onCall, onClose }) {
  const [digits, setDigits] = useState('');
  const inputRef = useRef(null);
  const longPressRef = useRef(null);
  const firedRef = useRef(false);
  const handleStartYRef = useRef(null);

  const press = (d) => {
    setDigits(prev => prev + d);
    // Move cursor to end after keypad press
    setTimeout(() => {
      const el = inputRef.current;
      if (el) { el.selectionStart = el.selectionEnd = el.value.length + 1; }
    }, 0);
  };
  const backspace = () => setDigits(prev => prev.slice(0, -1));

  // Handle direct input (typing, paste, editing)
  const onInputChange = (e) => {
    setDigits(cleanPhone(e.target.value));
  };

  const handleCall = () => {
    if (digits.trim()) onCall(digits.trim());
  };

  // ── Long-press: hold "0" → "+" ──
  const onKeyTouchStart = useCallback((digit, sub) => {
    firedRef.current = false;
    if (digit === '0' && sub === '+') {
      longPressRef.current = setTimeout(() => {
        firedRef.current = true;
        setDigits(prev => prev + '+');
        if (navigator.vibrate) navigator.vibrate(10);
      }, LONG_PRESS_MS);
    }
  }, []);

  const onKeyTouchEnd = useCallback((digit) => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
    if (firedRef.current) return;
    press(digit);
  }, []);

  // ── Handle drag-to-close (preventDefault to beat pull-to-refresh) ──
  const onHandleTouchStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleStartYRef.current = e.touches[0].clientY;
  };
  const onHandleTouchMove = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const onHandleTouchEnd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (handleStartYRef.current === null) return;
    const dy = e.changedTouches[0].clientY - handleStartYRef.current;
    handleStartYRef.current = null;
    if (dy > 50) onClose();
  };

  return (
    <div style={DS.backdrop} onClick={onClose}>
      <div style={DS.container} onClick={e => e.stopPropagation()}>
        {/* Handle — swipe down to close */}
        <div
          style={DS.handleArea}
          onTouchStart={onHandleTouchStart}
          onTouchMove={onHandleTouchMove}
          onTouchEnd={onHandleTouchEnd}
        >
          <div style={DS.handle} />
        </div>

        {/* Display — editable input */}
        <div style={DS.display}>
          <input
            ref={inputRef}
            type="tel"
            value={digits}
            onChange={onInputChange}
            placeholder="Номер"
            style={DS.input}
            autoComplete="off"
          />
          {digits && (
            <button onClick={backspace} style={DS.backspaceBtn}>
              <Delete size={22} color={colors.gray400} />
            </button>
          )}
        </div>

        {/* Keypad */}
        <div style={DS.keypad}>
          {KEYS.map((row, ri) => (
            <div key={ri} style={DS.row}>
              {row.map(({ digit, sub }) => (
                <button
                  key={digit}
                  onTouchStart={() => onKeyTouchStart(digit, sub)}
                  onTouchEnd={(e) => { e.preventDefault(); onKeyTouchEnd(digit); }}
                  onClick={(e) => {
                    if (!('ontouchstart' in window)) press(digit);
                  }}
                  style={DS.key}
                >
                  <span style={DS.keyDigit}>{digit}</span>
                  {sub && <span style={DS.keySub}>{sub}</span>}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* Call button */}
        <div style={DS.callRow}>
          <button onClick={handleCall} disabled={!digits.trim()}
            style={{ ...DS.callBtn, opacity: digits.trim() ? 1 : 0.4 }}>
            <Phone size={24} color={colors.white} />
          </button>
        </div>
      </div>
    </div>
  );
}

const DS = {
  backdrop: {
    position: 'fixed', inset: 0, zIndex: 55,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    fontFamily: FONT,
    padding: '0 8px max(12px, calc(env(safe-area-inset-bottom) + 8px))',
  },
  container: {
    width: '100%', maxWidth: 420,
    background: colors.white,
    borderRadius: radii.xl,
    padding: '0 24px 24px',
    animation: 'slideUp 0.3s ease-out',
    boxShadow: '0 -4px 30px rgba(0,0,0,0.12)',
  },
  handleArea: {
    padding: '12px 0 8px',
    cursor: 'grab',
    touchAction: 'none',
  },
  handle: {
    width: 36, height: 4, borderRadius: 2,
    background: colors.gray300, margin: '0 auto',
  },
  display: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: 48, marginBottom: 16, position: 'relative',
    padding: '0 40px',
  },
  input: {
    fontSize: 28, fontWeight: 300, color: colors.gray900,
    letterSpacing: 2, textAlign: 'center',
    fontFamily: FONT,
    background: 'none', border: 'none', outline: 'none',
    width: '100%', padding: 0,
    caretColor: colors.gray900,
  },
  backspaceBtn: {
    position: 'absolute', right: 0,
    background: 'none', border: 'none', cursor: 'pointer',
    padding: 8, display: 'flex', alignItems: 'center',
  },
  keypad: {
    display: 'flex', flexDirection: 'column', gap: 12,
  },
  row: {
    display: 'flex', justifyContent: 'center', gap: 20,
  },
  key: {
    width: 72, height: 72, borderRadius: '50%',
    background: colors.gray50, border: `1px solid ${colors.gray200}`,
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', fontFamily: FONT,
    transition: 'background 0.1s',
    userSelect: 'none', WebkitUserSelect: 'none',
  },
  keyDigit: {
    fontSize: 24, fontWeight: 400, color: colors.gray900, lineHeight: 1,
  },
  keySub: {
    fontSize: 9, fontWeight: 600, color: colors.gray400,
    letterSpacing: 2, marginTop: 2,
  },
  callRow: {
    display: 'flex', justifyContent: 'center', marginTop: 20,
  },
  callBtn: {
    width: 64, height: 64, borderRadius: '50%',
    background: colors.green, border: 'none',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', transition: 'opacity 0.15s',
  },
};
